import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import api from "../utils/axios";
import {
  Project,
  ProjectListResponse,
  ProjectResponse,
  CreateProjectData,
} from "../types/projectmanagement";
import { useAuth } from "../contexts/AuthContext";

// Query keys for React Query
export const projectManagementKeys = {
  all: ["project-management"] as const,
  list: () => [...projectManagementKeys.all, "list"] as const,
  detail: (id: number) => [...projectManagementKeys.all, "detail", id] as const,
} as const;

// API functions
const projectManagementAPI = {
  // Get projects page for the current user
  getProjects: async (
    page = 1,
    page_size = 10
  ): Promise<ProjectListResponse> => {
    const response = await api.get<ProjectListResponse>(
      `/v1/projects/?page=${page}&page_size=${page_size}`
    );
    return response.data;
  },

  // Get a specific project by ID
  getProject: async (id: number): Promise<Project> => {
    const response = await api.get<ProjectResponse>(`/v1/projects/${id}`);

    if (!response.data.success) {
      throw new Error(response.data.message || "Failed to fetch project");
    }

    return response.data.data;
  },

  // Create a new project
  createProject: async (projectData: CreateProjectData): Promise<Project> => {
    const response = await api.post<ProjectResponse>(
      "/v1/projects/",
      projectData
    );

    if (!response.data.success) {
      throw new Error(response.data.message || "Failed to create project");
    }

    return response.data.data;
  },

  // Update an existing project
  updateProject: async (
    id: number,
    updates: Partial<CreateProjectData>
  ): Promise<Project> => {
    const response = await api.patch<ProjectResponse>(
      `/v1/projects/${id}`,
      updates
    );

    if (!response.data.success) {
      throw new Error(response.data.message || "Failed to update project");
    }

    return response.data.data;
  },

  // Delete a project
  deleteProject: async (id: number): Promise<void> => {
    const response = await api.delete(`/v1/projects/${id}`);

    if (response.status !== 200 && response.status !== 204) {
      throw new Error("Failed to delete project");
    }
  },
};

// Custom hook for project management
export const useProjectManagement = () => {
  const queryClient = useQueryClient();
  const { isAuthenticated, token, isLoading: authLoading } = useAuth();

  // Infinite query for paginated projects
  const pageSize = 5;
  const {
    data,
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: projectManagementKeys.list(),
    queryFn: ({ pageParam = 1 }) =>
      projectManagementAPI.getProjects(pageParam, pageSize),
    initialPageParam: 1,
    getNextPageParam: (lastPage: ProjectListResponse) => {
      const { page, page_size, total } = lastPage;
      const maxPages = Math.ceil(total / page_size);
      return page < maxPages ? page + 1 : undefined;
    },
    enabled: isAuthenticated && !!token && !authLoading,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  // Flatten pages array into a single list
  const projects = data ? data.pages.flatMap((pg) => pg.data) : [];
  // For UI refetch indicator
  const isRefetching = isFetchingNextPage;

  // Memoized derived data
  const activeProjects = useMemo(
    () => projects.filter((project) => project.status === "active"),
    [projects]
  );

  const projectsByProvider = useMemo(() => {
    return projects.reduce((acc, project) => {
      const provider = project.primary_provider;
      if (!acc[provider]) {
        acc[provider] = [];
      }
      acc[provider].push(project);
      return acc;
    }, {} as Record<string, Project[]>);
  }, [projects]);

  // Mutation for creating projects
  const createProjectMutation = useMutation({
    mutationFn: projectManagementAPI.createProject,
    onSuccess: () => {
      // Invalidate list to fetch fresh pages
      queryClient.invalidateQueries({ queryKey: projectManagementKeys.list() });
    },
    onError: () => {
      // Invalidate and refetch on error
      queryClient.invalidateQueries({ queryKey: projectManagementKeys.list() });
    },
  });

  // Mutation for updating projects
  const updateProjectMutation = useMutation({
    mutationFn: ({
      id,
      updates,
    }: {
      id: number;
      updates: Partial<CreateProjectData>;
    }) => projectManagementAPI.updateProject(id, updates),
    onSuccess: () => {
      // Invalidate list to reflect updated project
      queryClient.invalidateQueries({ queryKey: projectManagementKeys.list() });
    },
    onError: () => {
      // Invalidate and refetch on error
      queryClient.invalidateQueries({ queryKey: projectManagementKeys.list() });
    },
  });

  // Mutation for deleting projects
  const deleteProjectMutation = useMutation({
    mutationFn: projectManagementAPI.deleteProject,
    onSuccess: () => {
      // Invalidate list to remove deleted project
      queryClient.invalidateQueries({ queryKey: projectManagementKeys.list() });
    },
    onError: () => {
      // Invalidate and refetch on error
      queryClient.invalidateQueries({ queryKey: projectManagementKeys.list() });
    },
  });

  // Callback functions
  const createProject = useCallback(
    (projectData: CreateProjectData) => {
      return createProjectMutation.mutateAsync(projectData);
    },
    [createProjectMutation]
  );

  const updateProject = useCallback(
    (id: number, updates: Partial<CreateProjectData>) => {
      updateProjectMutation.mutate({ id, updates });
    },
    [updateProjectMutation]
  );

  const deleteProject = useCallback(
    (id: number) => {
      deleteProjectMutation.mutate(id);
    },
    [deleteProjectMutation]
  );

  const refreshProjects = useCallback(() => {
    refetch();
  }, [refetch]);

  // Invalidate cache manually
  const invalidateCache = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: projectManagementKeys.all });
  }, [queryClient]);

  return {
    // Data
    projects,
    activeProjects,
    projectsByProvider,

    // Loading states
    isLoading: isLoading || authLoading,
    isRefetching,
    isError,
    error,

    // Mutation states
    isCreating: createProjectMutation.isPending,
    isUpdating: updateProjectMutation.isPending,
    isDeleting: deleteProjectMutation.isPending,

    // Actions
    createProject,
    updateProject,
    deleteProject,
    refreshProjects,
    invalidateCache,

    // Mutation objects (for advanced usage)
    createProjectMutation,
    updateProjectMutation,
    deleteProjectMutation,

    // Pagination controls
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
};

// Hook for a specific project
export const useProject = (projectId: number) => {
  const { isAuthenticated, token, isLoading: authLoading } = useAuth();

  const {
    data: project,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: projectManagementKeys.detail(projectId),
    queryFn: () => projectManagementAPI.getProject(projectId),
    enabled: isAuthenticated && !!token && !authLoading && !!projectId, // Only run when authenticated with token and projectId exists
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  return {
    project,
    isLoading: isLoading || authLoading,
    isError,
    error,
  };
};

export default useProjectManagement;
